import { useContext, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";

import { FROM_DATE_LIMIT } from "./DatePicker.consts";
import { clampRangeToMinDate } from "./DatePicker.utils";
import DateTimeContext from "../../../contexts/DateTimeContext";
import dayjs from "dayjs";

const usePredefinedDatePicker = (
  dates,
  setDates,
  fromDateLimit,
  allowSearchParams,
  fromKeyword,
  toKeyword,
  testId,
  presetOptions,
  minDate
) => {
  const { getTimeZoneDate, timeZone } = useContext(DateTimeContext);
  const dateOptions = useMemo(() => {
    if (presetOptions) return presetOptions;
    const now = dayjs().tz(timeZone);
    return [
      {
        key: "today",
        label: "Today",
        startDate: now.startOf("d"),
        endDate: now.endOf("d"),
        testId: `${testId}_today`
      },
      {
        key: "yesterday",
        label: "Yesterday",
        startDate: now.subtract(1, "d").startOf("d"),
        endDate: now.subtract(1, "d").endOf("d"),
        testId: `${testId}_yesterday`
      },
      {
        key: "last_7_days",
        label: "Last 7 Days",
        startDate: now.subtract(7, "d").startOf("d"),
        endDate: now.endOf("d"),
        testId: `${testId}_last_7_days`
      },
      {
        key: "last_14_days",
        label: "Last 14 Days",
        startDate: now.subtract(14, "d").startOf("d"),
        endDate: now.endOf("d"),
        testId: `${testId}_last_14_days`
      },
      {
        key: "last_30_days",
        label: "Last 30 Days",
        startDate: now.subtract(30, "d").startOf("d"),
        endDate: now.endOf("d"),
        testId: `${testId}_last_30_days`
      }
    ];
  }, [timeZone, testId, presetOptions]);

  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const [localDates, setLocalDates] = useState([...dates]);
  const [selectedDate, setSelectedDate] = useState(() =>
    dateOptions.find(
      dateOption =>
        dates?.[0] && dates?.[1]
        && dateOption.startDate.format() === dates[0].format()
        && dateOption.endDate.format() === dates[1].format()
    ));

  useEffect(() => {
    let tempDates = [...dates];

    if (allowSearchParams) {
      const paramsRange = clampRangeToMinDate(
        searchParams?.get(fromKeyword) ? dayjs(searchParams.get(fromKeyword)).tz(timeZone) : null,
        searchParams?.get(toKeyword) ? dayjs(searchParams.get(toKeyword)).tz(timeZone) : null,
        minDate
      );

      if (paramsRange) {
        tempDates = [paramsRange[0] ?? tempDates[0], paramsRange[1] ?? tempDates[1]];
      }
    }

    const predefinedDateSelected = dateOptions.find(
      dateOption =>
        tempDates
        && dateOption.startDate.format() === tempDates[0].format()
        && dateOption.endDate.format() === tempDates[1].format()
    );
    setSelectedDate(predefinedDateSelected);

    if ((tempDates[0] && tempDates[0].format() !== dates[0].format())
        || (tempDates[1] && tempDates[1]?.format() !== dates[1].format())) {
      setDates(tempDates[0], tempDates[1]);
    }

    // Outside the guard above: a caller that writes `dates` and the URL in one update (applying a
    // saved filter) leaves tempDates equal to dates, so the guard is false — but the custom From/To
    // fields still hold the pre-apply range, and editing one end would commit it with the stale other.
    setLocalDates(prev =>
      (prev?.[0]?.format() === tempDates[0]?.format() && prev?.[1]?.format() === tempDates[1]?.format())
        ? prev
        : tempDates);

    if (allowSearchParams && predefinedDateSelected) {
      updateSearchParams(predefinedDateSelected);
    }

  }, [dates, allowSearchParams, dateOptions]);

  const updateSearchParams = (dateOption) => {
    const nextFrom = dateOption.startDate.format();
    const nextTo = dateOption.endDate.format();
    if (searchParams.get(fromKeyword) === nextFrom && searchParams.get(toKeyword) === nextTo) return;
    searchParams.set(fromKeyword, nextFrom);
    searchParams.set(toKeyword, nextTo);
    setSearchParams(searchParams, { replace: true });
  };

  const handleOpenMenu = (event) => {
    setMenuAnchorEl(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setMenuAnchorEl(null);
  };

  const selectDate = (dateOption, closeMenu, isDateInvalid) => {
    if (closeMenu) {
      handleCloseMenu();
    }

    if (!isDateInvalid) {
      setLocalDates([dateOption.startDate, dateOption.endDate]);
      if (dateOption.startDate && dateOption.endDate && dateOption.startDate.valueOf() > dateOption.endDate.valueOf()) {
        return;
      }
      if (allowSearchParams) {
        updateSearchParams(dateOption);
      }
      setDates(dateOption.startDate, dateOption.endDate);
    }
  };

  const isBeforeLimit = date => {
    if (minDate && date?.isBefore(minDate, "d")) {
      return true;
    }
    if (fromDateLimit === FROM_DATE_LIMIT.ONE_YEAR) {
      return date < getTimeZoneDate().subtract(13, "months");
    }
    if (fromDateLimit === FROM_DATE_LIMIT.ONE_HUNDRED_EIGHTY_DAYS) {
      return date < getTimeZoneDate().subtract(180, "d");
    }
    return date < getTimeZoneDate().subtract(90, "d");
  };

  const shouldDisableFromDate = date => isBeforeLimit(date);

  const shouldDisableToDate = date =>
    isBeforeLimit(date)
    || (dates[0] && date.endOf("d").valueOf() < dates[0].startOf("d").valueOf());

  const isRangeInvalid = !!(localDates?.[0] && localDates?.[1] && localDates[0].valueOf() > localDates[1].valueOf());

  const isMenuOpen = Boolean(menuAnchorEl);

  const isMenuItemSelected = dateOption =>
    !!selectedDate && selectedDate.key === dateOption.key;

  return {
    dateOptions,
    selectedDate,
    localDates,
    isMenuOpen,
    menuAnchorEl,
    isMenuItemSelected,
    handleOpenMenu,
    handleCloseMenu,
    selectDate,
    shouldDisableFromDate,
    shouldDisableToDate,
    isRangeInvalid
  };

};

export default usePredefinedDatePicker;
